import { isValidElement } from 'react';

import { useDataTableContext } from './contexts/DataTableContext';
import { DataTableCell } from './DataTableCell';
import { DataTableRow } from './DataTableRow';
import { cn } from '../../../utils/cn';

type Props = {
    data: Array<Record<string, unknown>>;
    children: Array<React.ReactNode> | React.ReactNode;
    className?: string;
};
const makeShowable = (value: unknown) => {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object' || Array.isArray(value)) return JSON.stringify(value);
    if (typeof value === 'function') return 'Function';
    return 'Unknown';
};

const findDisplayValues = ({
    columnOrder,
    obj,
    parentPath,
}: {
    parentPath: string | null;
    obj: object;
    columnOrder: Array<string>;
}) => {
    const displayValues: Array<{ key: string; value: unknown }> = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(obj)) {
        const combinedKey = parentPath ? `${parentPath}.${key}` : key;
        if (typeof value === 'object' && value !== null) {
            const index = columnOrder.indexOf(combinedKey);
            if (index !== -1) {
                displayValues.push({ key: combinedKey, value });
            }
            const keyWithDot = columnOrder.find((entry) => {
                return entry.includes(`${key}.`);
            });
            if (keyWithDot) {
                displayValues.push(...findDisplayValues({ columnOrder, obj: value, parentPath: combinedKey }));
            }
            // eslint-disable-next-line no-continue
            continue;
        }
        const index = columnOrder.indexOf(combinedKey);
        if (index !== -1) displayValues.push({ key: combinedKey, value });
    }
    return displayValues;
};

const sortDisplayValues = (columnOrder: Array<string>, displayValues: Array<{ key: string; value: unknown }>) => {
    return columnOrder.map((orderKey) => {
        const found = displayValues.find(({ key }) => key === orderKey);
        if (found) {
            return found;
        }
        return { key: orderKey, value: '---' };
    });
};

export const DataTableSimple: React.FC<Props> = ({ children, className, data }) => {
    const { sortTableRows } = useDataTableContext();

    const parsedChildren: Array<React.ReactNode> = Array.isArray(children) ? children : [children];

    const columnOrder: string[] = parsedChildren.map((child) =>
        isValidElement<{ field: string }>(child) ? child.props.field : '',
    );

    return (
        <div
            className={cn(
                'bg-white border border-solid border-grey-outline-light rounded-md overflow-hidden',
                className,
            )}
        >
            <table className="w-full table-auto">
                <thead>
                    <tr>{parsedChildren.map((child) => child)}</tr>
                </thead>
                <tbody>
                    {data
                        ? sortTableRows(
                              data.map((obj) => (
                                  <DataTableRow payload={obj} key={JSON.stringify(obj)}>
                                      {(() => {
                                          const unsortedDisplayValues = findDisplayValues({
                                              columnOrder,
                                              obj,
                                              parentPath: null,
                                          });
                                          const sortedDisplayValues = sortDisplayValues(
                                              columnOrder,
                                              unsortedDisplayValues,
                                          );
                                          return sortedDisplayValues.map(({ key, value }) => (
                                              <DataTableCell key={key}>{makeShowable(value)}</DataTableCell>
                                          ));
                                      })()}
                                  </DataTableRow>
                              )),
                          )
                        : null}
                </tbody>
            </table>
        </div>
    );
};
